// Контекстная подсказка (мини-спринт C, уровень 2).
// Source: docs/specs/design-revision/c-onboarding.md §2.
//
// Замена карусели на входе. Объяснение приходит в момент, когда есть что
// объяснять: три из четырёх шагов гайда показывают данные, которых у нового
// человека ещё нет, и до первой загрузки это обещание, а не обучение.
//
// Условие показа — НЕ счётчик анализов, а факт первого показа блока (T-4).
// Прежние условия `analyses_count === 1` и `points.length === 2` не сработали
// бы ни у кого, кто воспользовался пакетной загрузкой: после неё счётчик сразу
// больше единицы, а точек у показателя может быть три и больше.

import { useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  disableAllCoachMarks,
  dismissCoachMark,
  shouldShowCoachMark,
  type CoachMarkId,
} from '@/lib/coach-storage';

export function CoachMark({
  id,
  title,
  children,
  /** Хвостик вверх — подсказка стоит ПОД объясняемым блоком. */
  pointer = 'up',
  className,
}: {
  id: CoachMarkId;
  title: string;
  children: React.ReactNode;
  pointer?: 'up' | 'down';
  className?: string;
}) {
  // Читаем один раз при монтировании: перечитывать в render незачем, а
  // инициализатор состояния не вызовет исключения — оно съедено в хранилище.
  const [visible, setVisible] = useState(() => shouldShowCoachMark(id));

  if (!visible) return null;

  return (
    <div className={cn('relative', className)}>
      {/* Хвостик к объясняемому блоку: подсказка обязана показывать, о чём
          она, иначе это просто ещё один текст на экране. */}
      <span
        aria-hidden="true"
        className={cn(
          'absolute left-6 h-3 w-3 rotate-45 border-primary bg-surface',
          pointer === 'up'
            ? '-top-1.5 border-l border-t'
            : '-bottom-1.5 border-b border-r',
        )}
      />
      <div className="rounded-surface border border-primary bg-surface p-3.5">
        <div className="mb-1 flex items-center gap-2">
          <Lightbulb className="h-4 w-4 flex-shrink-0 text-primary-text" aria-hidden="true" />
          <span className="text-hl-body font-semibold text-foreground">{title}</span>
        </div>
        <p className="text-hl-secondary leading-snug text-muted-foreground">{children}</p>
        <div className="mt-3 flex items-center gap-4">
          <button
            type="button"
            onClick={() => {
              dismissCoachMark(id);
              setVisible(false);
            }}
            className="text-hl-secondary font-semibold text-primary-text"
          >
            Понятно
          </button>
          {/* Человек, который не хочет обучения, не должен отказываться
              от него дважды: эта кнопка гасит весь слой. */}
          <button
            type="button"
            onClick={() => {
              disableAllCoachMarks();
              setVisible(false);
            }}
            className="text-hl-secondary text-muted-foreground"
          >
            Не показывать подсказки
          </button>
        </div>
      </div>
    </div>
  );
}
